import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { mondayOf, type TicketPortalMember } from '@catalyst-psa/shared';

import {
  useAddTicketPortalTimeEntry,
  useTicketPortalView,
  useTicketPortalWeekStatus,
} from './api.js';

/** Adds `days` to an ISO yyyy-mm-dd date (UTC-safe). */
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "Jun 7" from an ISO date (UTC-safe). */
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${d ?? 1}`;
}

/** "Jun 7 – Jun 13, 2026" from a week-start ISO date. */
function weekRangeLabel(weekStart: string): string {
  const end = addDaysIso(weekStart, 6);
  const [y] = end.split('-').map(Number);
  return `${shortDate(weekStart)} – ${shortDate(end)}, ${y}`;
}

/** "Mon, Aug 17, 5:00 PM" in the viewer's local timezone, from an ISO instant. */
function formatLockDeadline(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Context for the add-hours popup: which member/day was clicked. */
interface CellContext {
  member: TicketPortalMember;
  dayIso: string;
}

function DetailItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{value}</p>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value} h</p>
    </div>
  );
}

export function PublicTicketPortalPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useTicketPortalView(token);
  const addEntry = useAddTicketPortalTimeEntry(token);

  const [weekStart, setWeekStart] = useState<string>(() =>
    mondayOf(new Date().toISOString().slice(0, 10)),
  );
  const [activeCell, setActiveCell] = useState<CellContext | null>(null);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const weekStatusQuery = useTicketPortalWeekStatus(token, weekStart, true);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i)),
    [weekStart],
  );

  const view = viewQuery.data;

  /** Members whose timesheet for the displayed week is locked (approved/submitted). */
  const lockedMembers = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of weekStatusQuery.data?.members ?? []) {
      if (!m.editable) map.set(m.memberId, m.status ?? 'locked');
    }
    return map;
  }, [weekStatusQuery.data]);

  /** Existing hours already logged this week, keyed by `${memberId}|${dayIso}`. */
  const existingHours = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of weekStatusQuery.data?.entries ?? []) {
      const hours = Number(e.hours);
      if (!Number.isFinite(hours) || hours === 0) continue;
      map.set(`${e.memberId}|${e.workDate}`, hours);
    }
    return map;
  }, [weekStatusQuery.data]);

  const handleAdd = async (hours: string, note: string): Promise<void> => {
    if (!activeCell) return;
    const memberName = activeCell.member.displayName;
    await addEntry.mutateAsync({
      memberId: activeCell.member.id,
      workDate: activeCell.dayIso,
      hours,
      notes: note,
    });
    setActiveCell(null);
    setFeedback({ tone: 'ok', message: `Added ${hours} h for ${memberName}.` });
    await weekStatusQuery.refetch();
  };

  if (viewQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">
        Loading ticket…
      </div>
    );
  }

  if (viewQuery.isError || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This ticket link is not available. The link may have been regenerated. Please request a
            new link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{view.customerName ?? '—'}</h1>
          <h1 className="mt-0.5 text-xl font-semibold text-slate-900">{view.title}</h1>
        </div>

        {view.timeEntryLocked ? (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            This ticket is closed and locked for time tracking — new hours can no longer be added.
          </div>
        ) : view.statusIsClosed && view.timeEntryLockAt ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This ticket is closed. Hours can still be added until {formatLockDeadline(view.timeEntryLockAt)},
            when this week&apos;s timesheets are due.
          </div>
        ) : null}

        {/* Read-only details */}
        <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <DetailItem
            label="Ticket"
            value={view.referenceId?.trim() ? view.referenceId : `#${view.ticketNumber}`}
          />
          <DetailItem label="Status" value={view.statusName ?? '—'} />
          <DetailItem label="Type" value={view.ticketTypeName ?? '—'} />
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Assignees
            </p>
            {view.assignees.length === 0 ? (
              <p className="mt-0.5 text-sm text-slate-400">No members assigned</p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1">
                {view.assignees.map((m) => (
                  <span
                    key={m.id}
                    className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                  >
                    {m.displayName}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Hour totals across all logged time */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <StatCard label="Total hours" value={view.totalHours} />
          <StatCard label="Billable" value={view.totalBillableHours} />
          <StatCard label="Non-billable" value={view.totalNonBillableHours} />
        </div>

        {/* Hour entry (always visible) */}
        <div className="mt-8">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            {/* Week navigation */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setWeekStart((w) => addDaysIso(w, -7))}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                ‹ Prev week
              </button>
              <span className="px-2 text-sm font-medium text-slate-800">
                {weekRangeLabel(weekStart)}
              </span>
              <button
                type="button"
                onClick={() => setWeekStart((w) => addDaysIso(w, 7))}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                Next week ›
              </button>
              <button
                type="button"
                onClick={() => setWeekStart(mondayOf(new Date().toISOString().slice(0, 10)))}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                This week
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Member</th>
                    {days.map((iso, i) => (
                      <th key={iso} className="px-2 py-2 text-center font-medium">
                        <div>{DAY_LABELS[i]}</div>
                        <div className="font-normal text-slate-400">{shortDate(iso)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {view.assignees.map((member) => {
                    const lockedStatus = lockedMembers.get(member.id);
                    const locked = lockedStatus !== undefined || view.timeEntryLocked;
                    return (
                      <tr
                        key={member.id}
                        className={`border-b border-slate-50 ${locked ? 'opacity-50' : ''}`}
                      >
                        <td className="py-1.5 pr-3 text-slate-600">
                          {member.displayName}
                          {lockedStatus !== undefined ? (
                            <span className="ml-2 text-[11px] uppercase tracking-wide text-slate-400">
                              {lockedStatus}
                            </span>
                          ) : null}
                        </td>
                        {days.map((iso) => {
                          const existing = existingHours.get(`${member.id}|${iso}`);
                          if (locked) {
                            return (
                              <td key={iso} className="px-1 py-1 text-center">
                                <span className="inline-block h-7 w-12 leading-7 text-slate-400 tabular-nums">
                                  {existing ? existing : '—'}
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={iso} className="px-1 py-1 text-center">
                              <button
                                type="button"
                                onClick={() => setActiveCell({ member, dayIso: iso })}
                                title={existing ? `${existing} h logged — add more` : 'Add hours'}
                                className={`inline-flex h-7 w-12 items-center justify-center rounded border text-sm tabular-nums transition ${
                                  existing
                                    ? 'border-emerald-200 bg-emerald-50 font-medium text-emerald-700 hover:border-emerald-400'
                                    : 'border-slate-200 text-slate-300 hover:border-brand-400 hover:text-brand-500'
                                }`}
                              >
                                {existing ? existing : '+'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {view.assignees.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                This ticket has no assigned members to log hours for yet.
              </p>
            ) : null}

            {feedback ? (
              <p
                className={`mt-4 text-sm ${
                  feedback.tone === 'ok' ? 'text-emerald-600' : 'text-rose-600'
                }`}
              >
                {feedback.message}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {activeCell ? (
        <AddHoursModal
          cell={activeCell}
          ticketTitle={view.title}
          ticketRef={view.referenceId?.trim() ? view.referenceId : `#${view.ticketNumber}`}
          dayLabel={shortDate(activeCell.dayIso)}
          onClose={() => setActiveCell(null)}
          onSubmit={handleAdd}
        />
      ) : null}
    </div>
  );
}

/** Popup that collects hours + a required note before adding a single time entry. */
function AddHoursModal({
  cell,
  ticketTitle,
  ticketRef,
  dayLabel,
  onClose,
  onSubmit,
}: {
  cell: CellContext;
  ticketTitle: string;
  ticketRef: string;
  dayLabel: string;
  onClose: () => void;
  onSubmit: (hours: string, note: string) => Promise<void>;
}): JSX.Element {
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a number of hours greater than 0.');
      return;
    }
    if (!note.trim()) {
      setError('A note is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(String(value), note.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add hours.');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">Add hours</h2>
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-mono text-xs text-slate-400">{ticketRef}</span> {ticketTitle}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-1.5 text-lg font-bold text-white">
            {cell.member.displayName}
          </span>
          <span className="text-base font-semibold text-slate-700">{dayLabel}</span>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">Hours</label>
            <input
              type="number"
              min="0"
              step="0.25"
              inputMode="decimal"
              autoFocus
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Note <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was done?"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? 'Adding…' : 'Add hours'}
          </button>
        </div>
      </div>
    </div>
  );
}
